import { Responsive } from '@cian/ui-kit';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import * as style from './index.css';
import { EInternetStatus, setInternetStatus, TInternetStatus } from '../../actions/internetStatus';
import { ChatsErrorPage } from '../../components/ChatsErrorPage';
import { MobileLayout } from '../../components/MobileLayout';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { checkOnline } from '../../services/checkOnline';
import { checkUsersSession } from '../../services/checkUsersSession';
import { setBlinkingTitleWithTimeout } from '../../services/setBlinkingTitle';
import { startPollingMessages, stopPollingMessages } from '../../thunks/chatbot';
import { TAppState } from '../../types/appState';
import { IApplicationContext } from '../../types/applicationContext';
import { EHostType } from '../../types/hostType';
import { EMobileLayoutActiveSide } from '../../types/mobileSide';
import { TReduxActions } from '../../types/redux/actions';
import { ETelemetryEvents } from '../../types/telemetry';
import { EAgentAccountType } from '../../types/userData';
import { isSubAgentsChatsEnabled } from '../../utils/isSubAgentsChatsEnabled';
import { AlertContainer } from '../AlertContainer';
import { AuthNotificationContainer } from '../AuthNotificationContainer';
import { ChatActionModalContainer } from '../ChatActionModalContainer';
import { ChatListContainer } from '../ChatListContainer';
import { ChatWindowContainer } from '../ChatWindowContainer';
import { ChatbotFiltersModalContainer } from '../ChatbotFiltersModalContainer';
import { CustomActionModalContainer } from '../CustomActionModalContainer';
import { DailyrentReviewModalContainer } from '../DailyrentReviewModalContainer';
import { MasterTabsContainer } from '../MasterTabsContainer';
import { UserCardContainer } from '../UserCardContainer';

interface IAppProps {
  activeSide: EMobileLayoutActiveSide;
  isAuthenticated: boolean;
  internetStatus: string;
  showErrorPage?: boolean;
  isMasterTabsEnabled: boolean;
  isChatbotActive: boolean;
  context: IApplicationContext;
}

interface IAppDispatchProps {
  setBlinkingTitle(isWindowFocused: boolean): void;
  setInternetStatus(status: TInternetStatus): void;
  checkUsersSession(): Promise<void>;
  startPollingMessages(): Promise<void>;
  stopPollingMessages(): void;
}

type TAppStateProps = Pick<IAppProps, 'activeSide'>;

type TAppProps = IAppProps & IAppDispatchProps;

class PageComponent extends React.Component<TAppProps, object> {
  protected clearOnlineChecker: () => void;

  public componentDidMount() {
    window.addEventListener('focus', this.focusHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);

    const { context, showErrorPage, startPollingMessages } = this.props;

    const checkOnlinePeriod = context.config.get<number>('frontend-chats.checkOnlinePeriod') || 30000;
    const checkOnlineUrls = context.config.get<string[]>('frontend-chats.checkOnlineUrls') || [];

    this.clearOnlineChecker = checkOnline({
      period: checkOnlinePeriod,
      urls: checkOnlineUrls,
      onOnline: () => this.connectionCallback({ type: EInternetStatus.Online } as Event),
      onOffline: () => this.connectionCallback({ type: EInternetStatus.Offline } as Event),
    });

    if (showErrorPage) {
      if (context.logger) {
        context.logger.error('showErrorPage', {
          domain: 'src/shared/index.tsx',
          message: 'application started with error',
        });
      }

      if (context.telemetry) {
        context.telemetry.increment(ETelemetryEvents.ChatPageShowErrorPage);
      }
    }

    if (context.custom.hostType !== EHostType.Frame) {
      startPollingMessages();
    }
  }

  public componentWillUnmount() {
    const { setBlinkingTitle, stopPollingMessages } = this.props;
    // удаляет таймеры внутри мигающего заголовка
    setBlinkingTitle(true);
    window.removeEventListener('focus', this.focusHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);

    this.clearOnlineChecker();

    stopPollingMessages();
  }

  private connectionCallback = async (event: Event) => {
    const { setInternetStatus, checkUsersSession } = this.props;

    setInternetStatus(event.type as TInternetStatus);

    if (event.type === EInternetStatus.Online) {
      await checkUsersSession();
    }
  };

  /**
   * TODO: решение временное, сообщать о том, что пользователь разлогинился должен бэкенд.
   * Каждый раз, когда происходит фокус окна, запрашиваем токен сессии.
   * Если вернулась ошибка, то предполагаем, что пользователь разлогинился.
   * Если оффлайн или показана страница ошибки, делаем запрос после изменения этих флагов
   */
  private focusHandler = async () => {
    const { internetStatus, showErrorPage, checkUsersSession } = this.props;

    if (internetStatus === EInternetStatus.Offline || showErrorPage) {
      return;
    } else {
      await checkUsersSession();
    }
  };

  private visibilityHandler = async () => {
    const { setBlinkingTitle } = this.props;

    const isWindowFocused = typeof document !== 'undefined' && document.visibilityState === 'visible';

    setBlinkingTitle(isWindowFocused);
  };

  private handeRetry = () => {
    window.location.reload();
  };

  public render() {
    const { showErrorPage, isAuthenticated, isMasterTabsEnabled, isChatbotActive } = this.props;

    if (showErrorPage) {
      return (
        <div className={style['chats_container']}>
          <ChatsErrorPage onRetry={this.handeRetry} />
        </div>
      );
    }

    return (
      <>
        <Responsive phone tablet>
          <div className={style['chats_container']}>
            <MobileLayout
              activeSide={this.props.activeSide}
              leftView={<ChatListContainer />}
              centerView={<ChatWindowContainer />}
              rightView={<UserCardContainer />}
            />
            <AlertContainer />
            <ChatActionModalContainer />
            <CustomActionModalContainer />
          </div>
        </Responsive>
        <Responsive desktop>
          <div className={style['chats_container']}>
            <div className={style['list']}>{isMasterTabsEnabled ? <MasterTabsContainer /> : <ChatListContainer />}</div>
            <div className={style['chat']}>
              {isAuthenticated || isChatbotActive ? <ChatWindowContainer /> : <AuthNotificationContainer />}
              <AlertContainer />
            </div>
            <UserCardContainer />
            <ChatActionModalContainer />
            <CustomActionModalContainer />
          </div>
        </Responsive>
        <ChatbotFiltersModalContainer />
        <DailyrentReviewModalContainer />
      </>
    );
  }
}

export const PageContainer = connect<TAppStateProps, IAppDispatchProps, { context: IApplicationContext }, TAppProps>(
  (state: TAppState, props) => ({
    activeSide: state.mobileSide,
    showErrorPage: state.showErrorPage,
    isAuthenticated: state.user.isAuthenticated,
    internetStatus: state.internetStatus,
    isChatbotActive: currentOpenChatIDSelector(state) === CHATBOT_ID,
    isMasterTabsEnabled:
      state.user.isAuthenticated &&
      state.user.agentAccountType === EAgentAccountType.Agency &&
      isSubAgentsChatsEnabled(props.context.config),
  }),
  (dispatch: Dispatch<TReduxActions>) => ({
    setInternetStatus: (status: TInternetStatus) => dispatch(setInternetStatus(status)),
    checkUsersSession: () => dispatch<any>(checkUsersSession()),
    startPollingMessages: () => dispatch<any>(startPollingMessages()),
    stopPollingMessages: () => dispatch<any>(stopPollingMessages()),
    setBlinkingTitle: (isWindowFocused: boolean) => dispatch<any>(setBlinkingTitleWithTimeout({ isWindowFocused })),
  }),
  // tslint:disable-next-line:no-object-literal-type-assertion
  (stateProps: TAppStateProps, dispatchProps: IAppDispatchProps, ownProps: {}) =>
    ({
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
    } as TAppProps),
)(PageComponent);
